import { DateText, Table } from '@/components';
import type { Runner } from '@uchat-team-management/shared';
import { For, Show } from 'solid-js';
import { MockBadge, RunnerStatusBadge } from './StatusBadge';
import { BKK, bytes, duration, pct, secondsBetween, useNow } from './format';

export const Labels = (props: { labels: string[] }) => (
  <span class="flex flex-wrap gap-1">
    <For each={props.labels}>
      {(l) => (
        <span class="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[11px] text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300">
          {l}
        </span>
      )}
    </For>
  </span>
);

export const RunnersTable = (props: { runners: Runner[] }) => {
  const now = useNow(1000);
  return (
    <Table
      data={props.runners}
      rowKey={(r) => r.id}
      columns={[
        {
          key: 'name',
          header: 'Runner',
          render: (r) => (
            <span class="flex items-center gap-2 font-medium">
              {r.name}
              <MockBadge source={r.source} />
            </span>
          ),
        },
        { key: 'repo', header: 'Repo', render: (r) => <span class="text-xs">{r.repo}</span> },
        { key: 'labels', header: 'Labels', render: (r) => <Labels labels={r.labels} /> },
        {
          key: 'status',
          header: 'Status',
          render: (r) => <RunnerStatusBadge status={r.status} stale={r.stale} />,
        },
        {
          key: 'job',
          header: 'Current job',
          render: (r) => (
            <Show when={r.currentJob} fallback={<span class="text-neutral-400">—</span>}>
              {(j) => (
                <div class="min-w-0">
                  <a
                    href={j().htmlUrl ?? '#'}
                    target="_blank"
                    rel="noreferrer"
                    class="text-brand-600 hover:underline dark:text-brand-400"
                  >
                    {j().jobName ?? `run ${j().runId}`}
                  </a>
                  <Show when={j().workflowName}>
                    <div class="text-xs text-neutral-500">{j().workflowName}</div>
                  </Show>
                </div>
              )}
            </Show>
          ),
        },
        {
          key: 'started',
          header: 'Started · running for',
          render: (r) => (
            <Show when={r.currentJob?.startedAt} fallback={<span class="text-neutral-400">—</span>}>
              {(at) => (
                <div class="text-xs tabular-nums">
                  <div>
                    <DateText value={at()} fallbackTimeZone={BKK.fallbackTimeZone} />
                  </div>
                  <div class="text-neutral-500">{duration(secondsBetween(at(), null, now()))}</div>
                </div>
              )}
            </Show>
          ),
        },
        {
          key: 'proc',
          header: 'Runner CPU · RAM',
          align: 'right',
          render: (r) => (
            <Show
              when={r.proc}
              fallback={
                <span class="text-xs text-neutral-400" title="Needs the agent on this machine">
                  —
                </span>
              }
            >
              {(p) => (
                <span
                  class="text-xs tabular-nums"
                  title="Measured process tree of this runner (listener, worker, job steps)"
                >
                  {pct(p().cpuPct)} · {bytes(p().rssBytes)}
                </span>
              )}
            </Show>
          ),
        },
      ]}
    />
  );
};
